import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { ordersApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft, 
  Package, 
  User, 
  MapPin, 
  CreditCard, 
  Truck, 
  XCircle, 
  RefreshCcw,
  Loader2,
  CheckCircle,
  Clock,
  Home,
  LogOut
} from 'lucide-react';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAdmin();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchOrder();
  }, [orderId, isAuthenticated, navigate]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await ordersApi.getById(orderId);
      setOrder(response.data);
      setError(null);
    } catch (err) {
      setError('Order not found');
      console.error('Failed to fetch order:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdating(true);
      const response = await ordersApi.updateStatus(orderId, newStatus);
      setOrder(response.data);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'shipped':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'paid':
      case 'processing':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'refunded':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle size={16} />;
      case 'shipped':
        return <Truck size={16} />;
      case 'paid':
      case 'processing':
        return <Clock size={16} />;
      case 'cancelled':
        return <XCircle size={16} />;
      case 'refunded':
        return <RefreshCcw size={16} />;
      default:
        return <Package size={16} />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2d4a47]" size={40} />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{error || 'Order not found'}</p>
        <Button onClick={() => navigate('/admin/dashboard')} variant="outline">
          <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-[#2d4a47] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-serif italic">eden</h1>
              <span className="text-sm opacity-70">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm hover:underline flex items-center gap-1">
                <Home size={16} /> View Site
              </Link>
              <button onClick={handleLogout} className="text-sm hover:underline flex items-center gap-1">
                <LogOut size={16} /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button & Header */}
        <div className="mb-6">
          <Button 
            onClick={() => navigate('/admin/dashboard')} 
            variant="ghost" 
            className="mb-4"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-serif">Order Details</h1>
              <p className="text-gray-500 font-mono mt-1">#{order.id}</p>
            </div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${getStatusColor(order.status)}`}>
              {getStatusIcon(order.status)}
              <span className="font-medium capitalize">{order.status}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Package size={20} className="text-[#2d4a47]" /> Order Items
              </h2>
              <div className="divide-y">
                {order.items?.map((item, index) => (
                  <div key={index} className="py-4 first:pt-0 last:pb-0 flex items-center gap-4">
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{item.name}</h3>
                      {item.variant && (
                        <p className="text-sm text-gray-500">Size: {item.variant}</p>
                      )}
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                      <p className="text-sm text-gray-500">${item.price?.toFixed(2)} each</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Totals */}
              <div className="border-t mt-4 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span>${order.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Shipping</span>
                  <span>${order.shipping?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tax</span>
                  <span>${order.tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-medium text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-[#2d4a47]">${order.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <User size={20} className="text-[#2d4a47]" /> Customer Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Contact Details</h3>
                  <p className="text-gray-900">
                    {order.customer?.firstName} {order.customer?.lastName}
                  </p>
                  <p className="text-gray-600">{order.customer?.email}</p>
                  {order.customer?.phone && (
                    <p className="text-gray-600">{order.customer?.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <MapPin size={20} className="text-[#2d4a47]" /> Shipping Address
              </h2>
              {order.customer?.address ? (
                <div className="text-gray-600">
                  <p className="text-gray-900 font-medium">
                    {order.customer.firstName} {order.customer.lastName}
                  </p>
                  <p>{order.customer.address}</p>
                  <p>
                    {order.customer.city}, {order.customer.state} {order.customer.zip}
                  </p>
                  <p>{order.customer.country}</p>
                </div>
              ) : (
                <p className="text-gray-500">No shipping address provided</p>
              )}
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <CreditCard size={20} className="text-[#2d4a47]" /> Payment Information
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment Status</span>
                  <span className={`font-medium ${order.paymentStatus === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {order.paymentStatus || 'Pending'}
                  </span>
                </div>
                {order.paymentIntentId && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Payment ID</span>
                    <span className="font-mono text-xs">{order.paymentIntentId}</span>
                  </div>
                )}
                {order.stripeSessionId && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Stripe Session</span>
                    <span className="font-mono text-xs truncate max-w-[200px]">{order.stripeSessionId}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Timeline */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-medium mb-4">Order Timeline</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span>{formatDate(order.createdAt)}</span>
                </div>
                {order.updatedAt && order.updatedAt !== order.createdAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last Updated</span>
                    <span>{formatDate(order.updatedAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Update Status */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-medium mb-4">Update Status</h2>
              <div className="space-y-3">
                <Button
                  onClick={() => handleStatusUpdate('shipped')}
                  disabled={updating || order.status === 'shipped'}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {updating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Truck size={16} className="mr-2" />}
                  Mark as Shipped
                </Button>
                
                <Button
                  onClick={() => handleStatusUpdate('delivered')}
                  disabled={updating || order.status === 'delivered'}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  {updating ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle size={16} className="mr-2" />}
                  Mark as Delivered
                </Button>

                <div className="border-t pt-3 mt-3">
                  <p className="text-xs text-gray-500 mb-2">Other Actions</p>
                  
                  <Button
                    onClick={() => handleStatusUpdate('cancelled')}
                    disabled={updating || order.status === 'cancelled'}
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50 mb-2"
                  >
                    {updating ? <Loader2 className="animate-spin mr-2" size={16} /> : <XCircle size={16} className="mr-2" />}
                    Cancel Order
                  </Button>
                  
                  <Button
                    onClick={() => handleStatusUpdate('refunded')}
                    disabled={updating || order.status === 'refunded'}
                    variant="outline"
                    className="w-full border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    {updating ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCcw size={16} className="mr-2" />}
                    Mark as Refunded
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-medium mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.print()}
                >
                  Print Order
                </Button>
                {order.customer?.email && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open(`mailto:${order.customer.email}?subject=Order ${order.id}`)}
                  >
                    Email Customer
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
